// ProjectPreviewModal.tsx
import React, {useEffect, useRef, useState} from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Text,
  Spinner,
} from '@chakra-ui/react';
import VideoJS from '../MediaPlayer.tsx';
import {getProjectStreamLinks} from "../../services/projectService.ts";
import {IProjectStreamLinks} from "../../models/interfaces/projectStreamLinks.interface.ts";
import {ISubtitleTrack} from "../../models/interfaces/subtitleTrack.interface.ts";
import {IStreamLink} from "../../models/interfaces/streamLinks.interface.ts";
import iso6391 from "iso-639-1";

interface ProjectPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string | null;
}

const ProjectPreviewModal = React.memo(({isOpen, onClose, projectId}: ProjectPreviewModalProps) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [subtitlesTracks, setSubtitlesTracks] = useState<ISubtitleTrack[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const lastProjectIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen || !projectId || projectId === lastProjectIdRef.current) return;

    const fetchStreamLinks = async () => {
      setIsLoading(true);
      try {
        const projectStreamLinks: IProjectStreamLinks = await getProjectStreamLinks(projectId);
        setVideoUrl(`${projectStreamLinks.video.url}?${projectStreamLinks.video.sasToken}`);
        setSubtitlesTracks(
          Object.keys(projectStreamLinks.subtitles).map((languageCode: string, index: number) => {
            const languageStreamLink: IStreamLink = projectStreamLinks.subtitles[languageCode];
            return {
              src: `${languageStreamLink.url}?${languageStreamLink.sasToken}`,
              label: iso6391.getName(languageCode),
              srclang: languageCode,
              default: index === 0,
            };
          })
        );
        lastProjectIdRef.current = projectId; // 🧠 Mark as loaded
      } catch (error) {
        console.error('Failed to load video stream links:', error);
        setVideoUrl(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStreamLinks().then(() => console.debug("Fetched"));
  }, [isOpen, projectId]);

  const videoJsOptions = {
    autoplay: true,
    controls: false,
    responsive: true,
    fluid: true,
    sources: videoUrl
      ? [
        {
          src: videoUrl,
          type: 'video/mp4',
        },
      ]
      : [],
  };

  if (!projectId) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="50%"
      isCentered
      motionPreset="slideInBottom"
      autoFocus={false}
      trapFocus={false}
    >
      <ModalOverlay/>
      <ModalContent
        bg="black"
        w="65%"
        h="85%"
        p={0}
        m={0}
      >
        {isLoading ? (
          <Spinner size="xl" color="white" alignSelf="center"/>
        ) : videoUrl ? (
          <VideoJS options={videoJsOptions} subtitles={subtitlesTracks}/>
        ) : (
          <Text color="white">No video available.</Text>
        )}
      </ModalContent>
    </Modal>
  );
});

export default ProjectPreviewModal;